import type { TFunction } from "i18next";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { Button as ButtonV2 } from "../../../../Button";
import { BaseAlertTriangleIcon, CrossIcon, PlusIcon } from "../../../../Icons";
import { SearchInputV2 } from "../../../../SearchInputV2";
import { Typo } from "../../../../Typo";
import { type LookupResult, mockLookupSubIb } from "../../data";
import { SubIbStatusTag } from "../../SubIbStatusTag";
import { WizardFooter } from "../WizardFooter";

const ERROR_KEYS: Record<string, string> = {
  invalid_email: "subIBReport_lookupErrorInvalidEmail",
  self_lookup: "subIBReport_lookupErrorSelf",
  server_error: "subIBReport_lookupErrorServer",
  not_found: "subIBReport_lookupErrorNotFound",
  no_rebate_account: "subIBReport_lookupErrorNoRebateAccount",
  // Phase 1 has no rejected state of its own — a rejected Sub IB reads as not
  // found, as it did before the API told the two apart.
  rejected: "subIBReport_lookupErrorNotFound",
  kyc_pending: "subIBReport_lookupErrorKycPending",
  rate_limited: "subIBReport_lookupErrorRateLimited",
  duplicate: "subIBReport_lookupErrorDuplicate",
};

const ERROR_DEFAULTS: Record<string, string> = {
  invalid_email: "Please enter a valid email address",
  self_lookup: "You can't add yourself as a Sub IB",
  server_error:
    "Something went wrong checking that email. Try again in a moment.",
  not_found: "No partner found with this email",
  no_rebate_account:
    "This Sub IB's rebate account isn't set up yet. Try again in an hour, or contact support@pepperstonepartners.com if this problem persists.",
  rejected: "No partner found with this email",
  kyc_pending:
    "{{name}} hasn't completed their profile yet. Remind them to finish so you can add them to rebate programs and start referring clients together.",
  // {{seconds}} is filled from the live countdown (RATE_LIMIT_COOLDOWN_S) —
  // see `activeErrMsg` below. Keep the placeholder in every language.
  rate_limited: "Too many search attempts. Try again in {{seconds}}s.",
  // Entering the already-selected partner in the second field. Previously
  // reported as `self_lookup` ("You can't add yourself as a Sub IB"), which is
  // simply the wrong explanation.
  duplicate: "This Sub IB is already selected",
};

/**
 * The detailed error set: a distinct message for a rejected Sub IB, and
 * reworded no-rebate-account and server-error copy (PART-4481). Gated by
 * `part-4039-master-sub-ib-phase-2`; remove these two maps once that flag is
 * retired.
 */
const DETAILED_ERROR_KEYS: Record<string, string> = {
  server_error: "subIBReport_lookupErrorServerRetry",
  no_rebate_account: "subIBReport_lookupErrorRebateAccountInactive",
  rejected: "subIBReport_lookupErrorRejected",
};

const DETAILED_ERROR_DEFAULTS: Record<string, string> = {
  server_error: "We couldn't check that email. Try again in a moment.",
  no_rebate_account:
    "This Sub IB doesn't have an active rebate account. They'll need to contact support@pepperstonepartners.com to reactivate it.",
  rejected:
    "This Sub IB was rejected. They need to reapply in order for you to add them to a program.",
};

/**
 * Abuse guard for the gate. The cooldown trips on the failure *after* the limit —
 * "more than 5 consecutive failed lookups" per the AC, so the 6th. Matches
 * `mockLookupSubIb`'s own counter (`data.ts`), which uses the same
 * greater-than comparison, so mock and real API behave identically.
 */
const CONSECUTIVE_FAILURE_LIMIT = 5;
const RATE_LIMIT_COOLDOWN_S = 30;

/**
 * Whether a failed lookup counts toward the abuse guard.
 *
 * Decided on the **HTTP status**, not the error code: a 5xx is our outage rather
 * than the user probing for valid addresses, so it must never be what locks them
 * out — but a 4xx that fell through to the generic message is still a request the
 * user made, so it counts. A missing status on a `server_error` means the request
 * never got a response (offline, DNS, timeout) and is treated like a 5xx; a
 * missing status on any other code is a client-side outcome (invalid format,
 * duplicate) and counts.
 *
 * `rate_limited` is exempt as the cooldown reporting itself, not a new failure.
 *
 * Broader than AC 2.7's three-error list, which omitted self-lookups and
 * duplicates — both of which are retryable probes worth rate-limiting.
 */
const countsTowardRateLimit = (
  res: Extract<LookupResult, { matched: false }>,
): boolean => {
  if (res.error === "rate_limited") return false;
  if (res.status !== undefined) return res.status < 500;
  return res.error !== "server_error";
};

/**
 * The rate-limit error persists on screen until the cooldown finishes —
 * editing the email no longer clears it (every other gate error still clears
 * on edit, per the design worklog: "put the wait where the reason is").
 */
const SEARCH_BLOCKING_ERRORS = new Set(["rejected", "no_rebate_account"]);

const clearsOnEdit = (res: LookupResult | null): boolean =>
  !(res && !res.matched && res.error === "rate_limited");

/**
 * "Already your Sub IB · N of M programs active" — the canvas pluralises on the
 * *total* program count, not the active one, and counts archived programs in
 * that total (`activeHoidCount + inactiveHoidCount`). Two whole sentences rather
 * than an interpolated "program{s}" so each stays translatable.
 */
const existingProgramsLabel = (
  existing: {
    activeHoidCount: number;
    inactiveHoidCount: number;
  },
  t: TFunction,
): string => {
  return t("subIBReport_lookupExisting", {
    count: existing.activeHoidCount + existing.inactiveHoidCount,
    active: existing.activeHoidCount,
    defaultValue_one: "Your Sub IB · {{active}} of {{count}} program active",
    defaultValue_other: "Your Sub IB · {{active}} of {{count}} programs active",
  });
};

type LookupFieldProps = {
  label: string;
  email: string;
  onEmailChange: (email: string) => void;
  errMsg: string | null;
  onSearch: () => void;
  isMobile?: boolean;
};

/**
 * The gate's single search field. Canvas `.wiz-search` — one input that always
 * sits at the top of the gate, never in a partner's slot, so pinning a partner
 * doesn't move it (`.wiz-gate` renders the field, then `.wiz-gate__list`).
 *
 * `autoFocus` unconditionally: the field only renders while a slot is waiting to
 * be filled, which is exactly when it should hold focus.
 */
export const LookupField = ({
  label,
  email,
  onEmailChange,
  errMsg,
  onSearch,
  isMobile,
}: LookupFieldProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isMobile) inputRef.current?.focus();
  }, [isMobile]);

  return (
    <div className="flex flex-col gap-2">
      <SearchInputV2
        ref={inputRef}
        data-testid="rebate_programs_lookup_email_input"
        type="email"
        value={email}
        onChange={onEmailChange}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onSearch();
          }
        }}
        placeholder={t("subIBReport_lookupPlaceholder", "Search email")}
        aria-label={label}
        error={!!errMsg}
        clearable={false}
        autoFocus
      />
      {errMsg && (
        <span className="flex items-center gap-1 text-text-negative-default">
          <BaseAlertTriangleIcon
            className="h-4 w-4 shrink-0"
            aria-hidden="true"
          />
          <Typo
            element="span"
            type="bodyXS"
            textColor="text-text-negative-default"
          >
            {errMsg}
          </Typo>
        </span>
      )}
    </div>
  );
};

/** A resolved partner in the Selected Sub IBs list. Canvas `PinnedCard`. */
const PinnedCard = ({
  result,
  onRemove,
  phase2Enabled,
  isMobile,
}: {
  result: Extract<LookupResult, { matched: true }>;
  onRemove?: () => void;
  phase2Enabled?: boolean;
  isMobile?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-surface-brand-subtle p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            {/* Same scale on both viewports, per design QA. */}
            <Typo element="span" type="labelSM">
              {result.partner.displayName}
            </Typo>
            {phase2Enabled && result.partner.kycStatus === "Pending" && (
              <SubIbStatusTag status="pendingKYC" isMobile={isMobile} />
            )}
          </div>
          <Typo element="span" type="bodySM" textColor="text-text-secondary">
            {result.partner.email}
          </Typo>
          <Typo element="span" type="bodyXS" textColor="text-text-link-default">
            {result.existingRelationship.isExistingSubIb
              ? existingProgramsLabel(result.existingRelationship, t)
              : t("subIBReport_lookupNew", "Not your Sub IB yet")}
          </Typo>
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={t("subIBReport_lookupRemove", "Remove {{name}}", {
              name: result.partner.displayName,
            })}
            // Mirrors the wizard modal's own close affordance (`.modal__close`:
            // 24px glyph in a 32px box, rounded-lg, overlay tint on hover),
            // scaled to this card's 16px icon so the 4px inset matches. The
            // tint replaces the previous icon-colour swap, which the modal
            // close doesn't do.
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-icon-secondary transition-colors hover:bg-overlay-opacity-state-hover"
          >
            <CrossIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
};

type LookupStepProps = {
  title: string;
  /**
   * Resolves a Sub IB by email. Injected by the portal (real API adapter over
   * `useGetSubIbLookup`); defaults to the mock so Storybook / vibe-coding and
   * tests work without a portal hook.
   */
  lookupSubIb?: (email: string) => Promise<LookupResult>;
  /**
   * Partners pinned the last time the gate was left. Re-seeds the list when the
   * user comes Back from the first committed step — this step unmounts on every
   * step change, so without this the gate would return empty.
   */
  initialResults?: LookupResult[];
  /** Reports whether the gate holds unsaved input, for the discard confirmation. */
  onDirtyChange?: (dirty: boolean) => void;
  onLineupTouched?: () => void;
  onContinue: (results: LookupResult[]) => void;
  onCancel: () => void;
  isMobile?: boolean;
  footerContainer?: Element | null;
  /** masterSubIbPhase2: the Pending tag beside a pending-KYC match, the detailed error copy, and the Search lock. */
  phase2Enabled?: boolean;
};

export const LookupStep = ({
  title,
  lookupSubIb = mockLookupSubIb,
  initialResults,
  onDirtyChange,
  onLineupTouched,
  onContinue,
  onCancel,
  isMobile,
  footerContainer,
  phase2Enabled = false,
}: LookupStepProps) => {
  const { t } = useTranslation();

  const [firstEmail, setFirstEmail] = useState("");
  const [firstLooking, setFirstLooking] = useState(false);
  const [firstAttempted, setFirstAttempted] = useState(false);
  const [firstResult, setFirstResult] = useState<LookupResult | null>(
    () => initialResults?.[0] ?? null,
  );

  const [addingSecond, setAddingSecond] = useState(
    () => (initialResults?.length ?? 0) > 1,
  );
  const [secondEmail, setSecondEmail] = useState("");
  const [secondLooking, setSecondLooking] = useState(false);
  const [secondAttempted, setSecondAttempted] = useState(false);
  const [secondResult, setSecondResult] = useState<LookupResult | null>(
    () => initialResults?.[1] ?? null,
  );

  // Consecutive counted failures, and the seconds left before Search re-enables.
  const [failureCount, setFailureCount] = useState(0);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  // The rate-limit message clears itself once the wait is over — it's the
  // one gate error that doesn't wait for the next edit/search (design
  // worklog, 17 Aug: "cleared automatically when the timer reaches 0").
  useEffect(() => {
    if (cooldown > 0) return;
    const clearIfRateLimited = (r: LookupResult | null) =>
      r && !r.matched && r.error === "rate_limited" ? null : r;
    // oxlint-disable-next-line react/set-state-in-effect
    setFirstResult(clearIfRateLimited);
    // oxlint-disable-next-line react/set-state-in-effect
    setSecondResult(clearIfRateLimited);
  }, [cooldown]);

  const dirty =
    !!firstEmail.trim() ||
    !!secondEmail.trim() ||
    !!firstResult?.matched ||
    !!secondResult?.matched;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  /**
   * Applies a lookup outcome, folding in the abuse guard. Returns the result the
   * field should display — `rate_limited` replaces the underlying error once the
   * limit is passed, so the user is told why Search went away rather than just
   * seeing the same message again.
   *
   * Tripping the guard resets the counter: serving the cooldown *is* the penalty,
   * so the window that follows starts clean. Leaving the count above the limit
   * would re-lock on the very next failure, turning every later mistake into
   * another 30 seconds indefinitely.
   */
  const applyOutcome = (res: LookupResult): LookupResult => {
    if (res.matched) {
      setFailureCount(0);
      return res;
    }
    // The mock keeps its own counter and can rate-limit us directly; honour it so
    // Storybook and the portal behave the same.
    if (res.error === "rate_limited") {
      setFailureCount(0);
      setCooldown(RATE_LIMIT_COOLDOWN_S);
      return res;
    }
    if (!countsTowardRateLimit(res)) return res;
    const next = failureCount + 1;
    if (next > CONSECUTIVE_FAILURE_LIMIT) {
      setFailureCount(0);
      setCooldown(RATE_LIMIT_COOLDOWN_S);
      return { matched: false, error: "rate_limited" };
    }
    setFailureCount(next);
    return res;
  };

  const runLookup = async (
    email: string,
    pinned: { email: string; partnerId: string } | undefined,
    setLooking: (v: boolean) => void,
    setAttempted: (v: boolean) => void,
    setResult: (r: LookupResult | null) => void,
  ) => {
    if (cooldown > 0) return;
    if (!email.trim()) {
      // An empty submit is an invalid address like any other — it must say so
      // rather than silently clearing the field's state.
      setAttempted(true);
      setResult(applyOutcome({ matched: false, error: "invalid_email" }));
      return;
    }
    if (pinned && email.trim().toLowerCase() === pinned.email.toLowerCase()) {
      setAttempted(true);
      setResult({ matched: false, error: "duplicate" });
      return;
    }
    setLooking(true);
    const res = await lookupSubIb(email);
    setLooking(false);
    setAttempted(true);
    // Two addresses can resolve to one partner, so the id has to be checked
    // after the call — the email fast-path above can't catch that case.
    if (res.matched && pinned && res.partner.partnerId === pinned.partnerId) {
      setResult({ matched: false, error: "duplicate" });
      return;
    }
    const outcome = applyOutcome(res);
    setResult(outcome);
    if (outcome.matched) onLineupTouched?.();
  };

  // Canvas `wiz-gate__list`: matched partners sit under a group label naming how
  // many of the two allowed slots are filled.
  const pinnedCount = [firstResult?.matched, secondResult?.matched].filter(
    Boolean,
  ).length;

  /**
   * Which slot the single search field is currently filling — slot 0 until it
   * holds a match, then slot 1 once the user asks to add another. `null` means
   * every requested slot is resolved and the field is hidden.
   */
  const activeSlot: 0 | 1 | null = !firstResult?.matched
    ? 0
    : addingSecond && !secondResult?.matched
      ? 1
      : null;

  const isSecond = activeSlot === 1;
  const activeEmail = isSecond ? secondEmail : firstEmail;
  const activeLooking = isSecond ? secondLooking : firstLooking;
  const activeAttempted = isSecond ? secondAttempted : firstAttempted;
  const activeResult = isSecond ? secondResult : firstResult;

  const activeErrMsg =
    activeSlot !== null &&
    activeAttempted &&
    !activeLooking &&
    activeResult &&
    !activeResult.matched
      ? t(
          (phase2Enabled && DETAILED_ERROR_KEYS[activeResult.error]) ||
            ERROR_KEYS[activeResult.error],
          (phase2Enabled && DETAILED_ERROR_DEFAULTS[activeResult.error]) ||
            ERROR_DEFAULTS[activeResult.error],
          activeResult.error === "rate_limited"
            ? { seconds: cooldown }
            : activeResult.error === "kyc_pending"
              ? { name: activeResult.name }
              : {},
        )
      : null;

  // The already-pinned partner the second lookup must not duplicate.
  const pinnedFirst = firstResult?.matched
    ? {
        email: firstResult.partner.email,
        partnerId: firstResult.partner.partnerId,
      }
    : undefined;

  /**
   * Canvas `wantSearch`: Search only while the field is open *and* there is
   * something to search — nothing pinned yet, or text typed. An open-but-empty
   * second field therefore still offers Continue, so asking to add a second
   * partner and changing your mind can't trap you on the gate.
   */
  const wantSearch =
    activeSlot !== null && (pinnedCount === 0 || !!activeEmail.trim());

  // Retrying the same email can't change a rejected / no-account answer; editing it clears the error.
  const searchBlockedByError =
    phase2Enabled &&
    wantSearch &&
    activeErrMsg !== null &&
    activeResult !== null &&
    !activeResult.matched &&
    SEARCH_BLOCKING_ERRORS.has(activeResult.error);

  const handlePrimary = () => {
    if (searchBlockedByError) return;
    if (wantSearch) {
      if (isSecond) {
        void runLookup(
          secondEmail,
          pinnedFirst,
          setSecondLooking,
          setSecondAttempted,
          setSecondResult,
        );
        return;
      }
      void runLookup(
        firstEmail,
        undefined,
        setFirstLooking,
        setFirstAttempted,
        setFirstResult,
      );
      return;
    }
    const results = [firstResult, secondResult].filter(
      (r): r is LookupResult => !!r,
    );
    onContinue(results);
  };

  /** Removing slot 1 promotes slot 2 into it, so the list never has a hole. */
  const removeFirst = () => {
    onLineupTouched?.();
    setFirstResult(secondResult);
    setFirstEmail(secondEmail);
    setFirstAttempted(false);
    setSecondResult(null);
    setSecondEmail("");
    setSecondAttempted(false);
    setAddingSecond(false);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <div className="flex flex-col gap-3">
        <Typo element="h2" type={isMobile ? "headingLG" : "headingXL"}>
          {title}
        </Typo>
        <Typo element="p" type="bodySM" textColor="text-text-secondary">
          {t(
            "subIBReport_lookupSubtitle",
            "Search for and add up to 2 Sub IBs who are already registered as a Pepperstone partner",
          )}
        </Typo>
      </div>

      {/* Canvas `.wiz-gate { gap: 16px }` — the field first, then the selected
          list, then the add affordance. The field is a fixture at the top rather
          than living in a partner's slot, so pinning one doesn't move it. */}
      <div className="flex flex-col gap-4">
        {activeSlot !== null && (
          <LookupField
            label={
              isSecond
                ? t("subIBReport_lookupLabelSecond", "Second Sub IB email")
                : t("subIBReport_lookupLabel", "Email")
            }
            // Remounts between slots so the field starts empty and refocuses for
            // the second partner instead of carrying slot 1's draft over.
            key={activeSlot}
            email={activeEmail}
            onEmailChange={(v) => {
              if (isSecond) {
                setSecondEmail(v);
                if (clearsOnEdit(secondResult)) setSecondResult(null);
                return;
              }
              setFirstEmail(v);
              if (clearsOnEdit(firstResult)) setFirstResult(null);
            }}
            errMsg={activeErrMsg}
            // Enter mirrors the footer's primary action rather than always
            // searching (AC UI1.4).
            onSearch={handlePrimary}
            isMobile={isMobile}
          />
        )}

        {pinnedCount > 0 && (
          /* Canvas `.wiz-gate__list { gap: 8px }`. */
          <div className="flex flex-col gap-2">
            <Typo
              element="span"
              type="labelXS"
              textColor="text-text-tertiary"
              className="uppercase tracking-[0.04em]"
            >
              {t("subIBReport_lookupSelectedLabel", "{{count}} OF 2 SELECTED", {
                count: pinnedCount,
              })}
            </Typo>
            {firstResult?.matched && (
              <PinnedCard
                result={firstResult}
                onRemove={removeFirst}
                phase2Enabled={phase2Enabled}
                isMobile={isMobile}
              />
            )}
            {secondResult?.matched && (
              <PinnedCard
                result={secondResult}
                phase2Enabled={phase2Enabled}
                isMobile={isMobile}
                onRemove={() => {
                  onLineupTouched?.();
                  setSecondResult(null);
                  setSecondEmail("");
                  setSecondAttempted(false);
                  setAddingSecond(false);
                }}
              />
            )}
          </div>
        )}

        {firstResult?.matched && !addingSecond && (
          <button
            type="button"
            onClick={() => setAddingSecond(true)}
            className="inline-flex items-center gap-2 self-start py-1 text-text-link-default hover:underline"
          >
            <PlusIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            {/* Canvas `.wiz-gate__add typo-label-sm` — 600 weight in Plus Jakarta
                Sans. `bodySM` renders 400 in Roboto, so this is a label, not body. */}
            <Typo
              element="span"
              type="labelSM"
              textColor="text-text-link-default"
            >
              {t("subIBReport_lookupAddSecond", "Add Sub IB")}
            </Typo>
          </button>
        )}
      </div>

      {/* TODO: remove IIFE per "No IIFEs in JSX" (AGENTS.md) — pick basic/medium/complex tier */}
      {(() => {
        const footer = (
          <WizardFooter isMobile={isMobile}>
            <ButtonV2 variant="tonal" size="md" onClick={onCancel}>
              {t("common_cancel", "Cancel")}
            </ButtonV2>
            <ButtonV2
              data-testid="sub_ib_create_program_lookup_primary_button"
              variant="brand"
              size="md"
              isLoading={activeLooking}
              disabled={(cooldown > 0 && wantSearch) || searchBlockedByError}
              onClick={handlePrimary}
            >
              {wantSearch
                ? // The wait now lives in the error message (activeErrMsg), not
                  // the button — the button just stays disabled and keeps its
                  // plain label for the whole cooldown (design worklog, 17 Aug).
                  t("subIBReport_lookupSearch", "Search")
                : t("common_continue", "Continue")}
            </ButtonV2>
          </WizardFooter>
        );
        return footerContainer ? createPortal(footer, footerContainer) : footer;
      })()}
    </div>
  );
};
